import * as React from "react";
import { type UseFormReset } from "react-hook-form";
import { useLocation } from "react-router";

import {
  claimAgentDraftFor,
  type AgentDraft,
  type AgentRecord,
} from "@/shared/domain/agents";

import {
  emptyFormState,
  overlayDraftOnForm,
  parseSavedAgent,
  type SeededFrom,
  shouldSeedForm,
} from "../Agents/buildRequestTemplate";

import { type AgentFormValues } from "./agentFormSchema";

/** The part of a react-hook-form form this hook writes through. */
export interface SeedableForm {
  reset: UseFormReset<AgentFormValues>;
}

/**
 * Claim any pending `client_create_agent` draft for this form's route and seed
 * the form from it.
 *
 * What is seeded is decided by three pure functions that predate the form
 * (`parseSavedAgent`, `overlayDraftOnForm`, `shouldSeedForm`); this hook only
 * decides *when* the answer is written, and writes it with `reset`. An absent
 * `initial` is the create route and a present one is the edit route — the edit
 * page does not mount the form until the agent has landed, so there is no
 * "edit, but nothing loaded yet" state to guard against.
 *
 * Seeding is one-shot per (agent, proposal) pair rather than per agent. The
 * per-agent half is what keeps a refetch — a post-save cache invalidation, a
 * tab-focus refresh — from resetting over edits the user is in the middle of
 * typing. The per-proposal half is what lets a second AI proposal land. See
 * {@link shouldSeedForm}.
 *
 * Which drafts belong to which route is owned by {@link claimAgentDraftFor};
 * this hook only decides what to do with a claimed one. The claim is re-run on
 * every navigation, not just on mount and not just when the routed id changes.
 * React Router reuses one element across `/agents/:agentId/edit` navigations,
 * so a mount-only claim would miss the handoff whenever the user already had a
 * form open — which is the common case, since the AI runs in a side panel over
 * whatever page the user is on. Keying on the routed id alone still missed the
 * sharpest version of that: updating the agent the user is *already looking at*
 * pushes the unchanged URL, so nothing about the route identity moves and the
 * draft would sit in sessionStorage forever. `location.key` is the signal that
 * does move — React Router mints a fresh one even for a same-URL push.
 *
 * An unapplied claim survives a re-key on the same route. Navigations happen
 * for reasons that have nothing to do with the handoff (a tab change, a query
 * param), and re-claiming returns null once the draft has been consumed from
 * storage; overwriting the held claim with that null would re-seed the form
 * back to the saved values and discard a proposal mid-review.
 *
 * Claiming during render (rather than in an effect) is deliberate: the seed
 * effect below reads the claim in the same pass that observes it. Deferring the
 * claim to an effect would let the seed run first with the previous route's
 * draft and mark the agent as already seeded, so the fresh draft would never be
 * applied.
 *
 * Idempotency under React 18 StrictMode is owned by the draft-consume cache in
 * `agentDraft.ts`, not by this hook: StrictMode double-invokes render-phase
 * functions in development, so the claim runs twice back-to-back, and the cache
 * makes both runs see the same draft. See `agentDraft.ts` for the cache
 * lifecycle.
 */
export function useDraftSeededForm(
  form: SeedableForm,
  initial?: AgentRecord,
): void {
  const mode = initial === undefined ? "create" : "edit";
  const agentId = initial?.agent_id;
  const route = `${mode}:${agentId ?? ""}`;
  const { key: locationKey } = useLocation();
  // Keyed by route *and* navigation so both a param change and a same-URL
  // push re-claim. Holds the claim itself rather than a "have we claimed"
  // flag, so the value survives the re-renders between the claim and the
  // effect consuming it.
  const claimRef = React.useRef<{
    route: string;
    locationKey: string;
    draft: AgentDraft | null;
  } | null>(null);
  const previous = claimRef.current;
  const current =
    previous !== null &&
    previous.route === route &&
    previous.locationKey === locationKey
      ? previous
      : {
          route,
          locationKey,
          // Leaving the route abandons whatever it had pending; staying on it
          // keeps an unapplied claim alive across an unrelated navigation.
          draft:
            claimAgentDraftFor({ mode, agentId }) ??
            (previous !== null && previous.route === route
              ? previous.draft
              : null),
        };
  claimRef.current = current;
  const claimed = current.draft;

  const { reset } = form;
  const seededRef = React.useRef<SeededFrom | null>(null);
  React.useEffect(() => {
    if (!shouldSeedForm(seededRef.current, agentId ?? "", claimed)) {
      return;
    }
    seededRef.current = { agentId: agentId ?? "", draft: claimed };
    const base =
      initial === undefined ? emptyFormState() : parseSavedAgent(initial);
    // An empty draft overlays to the base unchanged, so the manual and
    // AI-handoff paths share one expression.
    reset(overlayDraftOnForm(base, claimed ?? {}));
  }, [reset, agentId, initial, claimed]);
}
